/**
 * WorksheetBatchIntakeForm — page10 Slice 2 (Q-T040): csoportos munkalap-beadás form.
 *
 * A Slice 1 contract-layerre (#469) épít: `useCreateWorksheetBatch` → `POST /worksheets/batch`.
 * Egy partnertől 1–26 gép egyetlen `intakeBatchId` alatt, al-számokkal. A submit a #470 BE-fix
 * óta stabil error-kódokra branch-el (400×3 + 409) — a MSW és a valódi BE 1:1 emittál.
 *
 * **Placement-agnoszt**: önálló, mountolható komponens — a route/wiring a Q-T040 placement-scope
 * ratify-re vár (Javo). A `onSuccess` callback adja vissza a `{ intakeBatchId, worksheets }`-t a
 * befogadó kontextusnak (lista-refetch / navigáció / drawer-close), így a scope-változás csak a
 * beágyazást mozdítja, a form-magot nem.
 */
import type { Partner } from '@/api/partners';
import {
  WorksheetPriority,
  WorksheetType,
  type CreateWorksheetBatchInput,
  type CreateWorksheetBatchItem,
  type WorksheetBatchResponse,
} from '@/api/worksheets';
import { PartnerSearchSelect } from '@/components/partners/PartnerSearchSelect';
import { useCreateWorksheetBatch } from '@/hooks/use-create-worksheet-batch';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Textarea,
} from '@kgc/ui';
import { useCallback, useMemo, useState } from 'react';

const MAX_ITEMS = 26;
const MIN_DEVICE_NAME = 2;
const MIN_FAULT = 5;

/** enum → i18n-kulcs (a `worksheet.type.*` / `worksheet.priority.*` namespace). FELARAS → `surcharge`. */
const TYPE_KEY: Record<WorksheetType, string> = {
  [WorksheetType.FIZETOS]: 'worksheet.type.fizetos',
  [WorksheetType.GARANCIALIS]: 'worksheet.type.garancialis',
  [WorksheetType.BERLESI]: 'worksheet.type.berlesi',
  [WorksheetType.KARBANTARTAS]: 'worksheet.type.karbantartas',
};
const PRIORITY_KEY: Record<WorksheetPriority, string> = {
  [WorksheetPriority.NORMAL]: 'worksheet.priority.normal',
  [WorksheetPriority.SURGOS]: 'worksheet.priority.surgos',
  [WorksheetPriority.FELARAS]: 'worksheet.priority.surcharge',
  [WorksheetPriority.GARANCIALIS]: 'worksheet.priority.garancialis',
  [WorksheetPriority.FRANCHISE]: 'worksheet.priority.franchise',
};

/** A #469 MSW + #470 BE specifikus error-kódjai → i18n-kulcs. Ismeretlen kód → generic. */
const ERROR_KEY: Record<string, string> = {
  WORKSHEET_BATCH_EMPTY: 'worksheet.intakeBatch.errors.empty',
  WORKSHEET_BATCH_TOO_MANY: 'worksheet.intakeBatch.errors.tooMany',
  WORKSHEET_BATCH_DUPLICATE_SERIAL: 'worksheet.intakeBatch.errors.duplicateSerial',
  WORKSHEET_DEVICE_ALREADY_ACTIVE: 'worksheet.intakeBatch.errors.deviceActive',
};

interface ItemDraft {
  deviceName: string;
  faultDescription: string;
  deviceSerialNumber: string;
  costLimit: string;
  internalNote: string;
}

const emptyItem = (): ItemDraft => ({
  deviceName: '',
  faultDescription: '',
  deviceSerialNumber: '',
  costLimit: '',
  internalNote: '',
});

export interface WorksheetBatchIntakeFormProps {
  /** Placement-agnoszt success-callback — a befogadó kontextus kapja a beadás eredményét. */
  onSuccess?: (result: WorksheetBatchResponse['data']) => void;
  /** Opcionális mégse — ha nincs, a Mégse gomb nem jelenik meg. */
  onCancel?: () => void;
}

export function WorksheetBatchIntakeForm({ onSuccess, onCancel }: WorksheetBatchIntakeFormProps) {
  const { t } = useTranslation();
  const { create, isLoading, error, errorCode } = useCreateWorksheetBatch();

  const [partner, setPartner] = useState<Partner | null>(null);
  const [type, setType] = useState<WorksheetType>(WorksheetType.FIZETOS);
  const [priority, setPriority] = useState<WorksheetPriority>(WorksheetPriority.NORMAL);
  const [items, setItems] = useState<ItemDraft[]>([emptyItem()]);
  const [result, setResult] = useState<WorksheetBatchResponse['data'] | null>(null);

  const addItem = useCallback(() => {
    setItems(prev => (prev.length >= MAX_ITEMS ? prev : [...prev, emptyItem()]));
  }, []);
  const removeItem = useCallback((index: number) => {
    setItems(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  }, []);
  const patchItem = useCallback((index: number, patch: Partial<ItemDraft>) => {
    setItems(prev => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }, []);

  const itemsValid = useMemo(
    () =>
      items.every(
        it =>
          it.deviceName.trim().length >= MIN_DEVICE_NAME &&
          it.faultDescription.trim().length >= MIN_FAULT
      ),
    [items]
  );
  const canSubmit =
    partner !== null && itemsValid && items.length >= 1 && items.length <= MAX_ITEMS && !isLoading;

  const handleSubmit = useCallback(async () => {
    if (partner === null) return;
    const payload: CreateWorksheetBatchInput = {
      partnerId: partner.id,
      type,
      priority,
      items: items.map<CreateWorksheetBatchItem>(it => ({
        deviceName: it.deviceName.trim(),
        faultDescription: it.faultDescription.trim(),
        ...(it.deviceSerialNumber.trim()
          ? { deviceSerialNumber: it.deviceSerialNumber.trim() }
          : {}),
        ...(it.costLimit.trim() ? { costLimit: Number(it.costLimit) } : {}),
        ...(it.internalNote.trim() ? { internalNote: it.internalNote.trim() } : {}),
      })),
    };
    try {
      const data = await create(payload);
      setResult(data);
      onSuccess?.(data);
    } catch {
      // errorCode / error a hook-state-ből jön — a UI arra branch-el (lentebb), itt no-op.
    }
  }, [partner, type, priority, items, create, onSuccess]);

  const handleReset = useCallback(() => {
    setPartner(null);
    setType(WorksheetType.FIZETOS);
    setPriority(WorksheetPriority.NORMAL);
    setItems([emptyItem()]);
    setResult(null);
  }, []);

  const errorMessage = useMemo(() => {
    if (errorCode) return t(ERROR_KEY[errorCode] ?? 'worksheet.intakeBatch.errors.generic');
    if (error) return t('worksheet.intakeBatch.errors.generic');
    return null;
  }, [errorCode, error, t]);

  // ---- Sikeres beadás nézet ----
  if (result !== null) {
    return (
      <div data-testid="worksheet-batch-intake-form" className="space-y-4">
        <Card data-testid="batch-success">
          <CardHeader>
            <CardTitle>{t('worksheet.intakeBatch.successTitle')}</CardTitle>
            <CardDescription>
              {t('worksheet.intakeBatch.successDesc', {
                count: result.worksheets.length,
                batchId: result.intakeBatchId,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul
              className="space-y-1 text-sm text-muted-foreground"
              data-testid="batch-success-list"
            >
              {result.worksheets.map(ws => (
                <li key={ws.id} data-testid="batch-success-row">
                  <span className="font-medium text-foreground">{ws.worksheetNumber}</span>
                  {' — '}
                  {ws.deviceName}
                </li>
              ))}
            </ul>
            <Button
              className="mt-4"
              variant="outline"
              data-testid="batch-new"
              onClick={handleReset}
            >
              {t('worksheet.intakeBatch.newBatch')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Beadás form ----
  return (
    <div data-testid="worksheet-batch-intake-form" className="space-y-6">
      <header className="space-y-1">
        <h2 className="text-xl font-semibold">{t('worksheet.intakeBatch.formTitle')}</h2>
        <p className="text-sm text-muted-foreground">{t('worksheet.intakeBatch.formDesc')}</p>
      </header>

      {/* Partner */}
      <Card>
        <CardHeader>
          <CardTitle>{t('worksheet.intakeBatch.partnerSection')}</CardTitle>
          <CardDescription>{t('worksheet.intakeBatch.partnerHint')}</CardDescription>
        </CardHeader>
        <CardContent>
          {partner !== null ? (
            <div
              className="flex items-center justify-between gap-4"
              data-testid="batch-selected-partner"
            >
              <span className="text-sm">
                {t('worksheet.intakeBatch.selectedPartner', { name: partner.name })}
              </span>
              <Button
                variant="ghost"
                size="sm"
                data-testid="batch-change-partner"
                onClick={() => setPartner(null)}
              >
                {t('worksheet.intakeBatch.changePartner')}
              </Button>
            </div>
          ) : (
            <PartnerSearchSelect onSelect={setPartner} value={partner} showRecent allowCreate />
          )}
        </CardContent>
      </Card>

      {/* Beadás-szintű beállítások: típus + prioritás */}
      <Card>
        <CardHeader>
          <CardTitle>{t('worksheet.intakeBatch.batchSettings')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="batch-type">{t('service.worksheetType')}</Label>
            <select
              id="batch-type"
              data-testid="batch-type"
              className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
              value={type}
              onChange={e => setType(e.target.value as WorksheetType)}
            >
              {(Object.values(WorksheetType) as WorksheetType[]).map(value => (
                <option key={value} value={value}>
                  {t(TYPE_KEY[value])}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="batch-priority">{t('common.priority')}</Label>
            <select
              id="batch-priority"
              data-testid="batch-priority"
              className="border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
              value={priority}
              onChange={e => setPriority(e.target.value as WorksheetPriority)}
            >
              {(Object.values(WorksheetPriority) as WorksheetPriority[]).map(value => (
                <option key={value} value={value}>
                  {t(PRIORITY_KEY[value])}
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Gépek (1–26) */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">{t('worksheet.intakeBatch.deviceList')}</h3>
          <span className="text-sm text-muted-foreground" data-testid="batch-item-count">
            {t('worksheet.intakeBatch.itemCount', { count: items.length })}
          </span>
        </div>

        {items.map((it, index) => (
          <Card key={index} data-testid={`batch-item-${index}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 py-3">
              <CardTitle className="text-base">
                {t('worksheet.intakeBatch.itemHeading', { index: index + 1 })}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                data-testid={`batch-item-${index}-remove`}
                disabled={items.length <= 1}
                onClick={() => removeItem(index)}
              >
                {t('worksheet.intakeBatch.removeItem')}
              </Button>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor={`batch-item-${index}-deviceName`}>
                  {t('service.deviceNameRequired')}
                </Label>
                <Input
                  id={`batch-item-${index}-deviceName`}
                  data-testid={`batch-item-${index}-deviceName`}
                  value={it.deviceName}
                  placeholder={t('service.deviceNamePlaceholder')}
                  onChange={e => patchItem(index, { deviceName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`batch-item-${index}-serial`}>{t('service.serialNumber')}</Label>
                <Input
                  id={`batch-item-${index}-serial`}
                  data-testid={`batch-item-${index}-serial`}
                  value={it.deviceSerialNumber}
                  placeholder={t('service.serialNumberPlaceholder')}
                  onChange={e => patchItem(index, { deviceSerialNumber: e.target.value })}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor={`batch-item-${index}-fault`}>
                  {t('service.faultDescriptionRequired')}
                </Label>
                <Textarea
                  id={`batch-item-${index}-fault`}
                  data-testid={`batch-item-${index}-fault`}
                  rows={3}
                  value={it.faultDescription}
                  placeholder={t('service.faultDescriptionPlaceholder')}
                  onChange={e => patchItem(index, { faultDescription: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`batch-item-${index}-costLimit`}>
                  {t('service.costLimitOptional')}
                </Label>
                <Input
                  id={`batch-item-${index}-costLimit`}
                  data-testid={`batch-item-${index}-costLimit`}
                  type="number"
                  min={0}
                  value={it.costLimit}
                  placeholder={t('worksheet.placeholder.estimatedCost')}
                  onChange={e => patchItem(index, { costLimit: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor={`batch-item-${index}-note`}>{t('service.internalNote')}</Label>
                <Input
                  id={`batch-item-${index}-note`}
                  data-testid={`batch-item-${index}-note`}
                  value={it.internalNote}
                  placeholder={t('service.internalOnlyPlaceholder')}
                  onChange={e => patchItem(index, { internalNote: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outline"
          data-testid="batch-add-item"
          disabled={items.length >= MAX_ITEMS}
          onClick={addItem}
        >
          {t('worksheet.intakeBatch.addItem')}
        </Button>
      </div>

      {errorMessage !== null && (
        <div
          role="alert"
          data-testid="batch-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <Button variant="outline" data-testid="batch-cancel" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        )}
        <Button data-testid="batch-submit" disabled={!canSubmit} onClick={handleSubmit}>
          {isLoading
            ? t('worksheet.intakeBatch.submitting')
            : t('worksheet.intakeBatch.submit', { count: items.length })}
        </Button>
      </div>
    </div>
  );
}
